'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { getTrackingParams, NumberInput, PhoneDisclosure, RangeSelectInput } from '@nwwp/acquisition-ui'
import { Button, Input } from '@nwwp/ui-shadcn'
import { useVeritonic } from '@nwwp/veritonic'
import { registerForWebinar } from '~/actions/register-for-webinar'
import { EQUITY_WEBINAR_SLUG, type WebinarLeadMagnetSlug } from '~/app/equity-webinar/constants'
import {
  ASSET_RANGE_OPTIONS,
  INCOME_RANGE_OPTIONS,
  type WebinarRegistration,
  WebinarRegistrationSchema,
} from '~/lib/schemas/webinar-registration-schema'

/** Sentence case per the design; the `funnel` button variant uppercases the pill in CSS. */
export const CTA_TEXT = 'Save my seat'

/** The design shows real labels above each select, not RangeSelectInput's sr-only default. */
const LABEL_CLASS = 'text-body-sm-bold text-foreground'

/** Design input chrome: 48px tall, 12px radius, hairline border. */
const SELECT_CLASS = 'h-12 rounded-xl border border-black/15 bg-white py-0 shadow-none'

export function WebinarRegistrationForm({
  leadMagnetSlug = EQUITY_WEBINAR_SLUG,
}: {
  leadMagnetSlug?: WebinarLeadMagnetSlug
}) {
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null)
  const { trackFlowComplete } = useVeritonic()

  const {
    control,
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<WebinarRegistration>({
    resolver: zodResolver(WebinarRegistrationSchema),
    defaultValues: { name: '', household_income_range: '', investable_assets_range: '', email: '', phone: '' },
  })

  const onSubmit = async (data: WebinarRegistration) => {
    try {
      const success = await registerForWebinar(data, getTrackingParams(), leadMagnetSlug)

      if (!success) {
        setError('root', { message: 'Something went wrong. Please try again.' })
        return
      }

      // Tracking is best-effort; never block the confirmation on it.
      try {
        trackFlowComplete()
      } catch (e) {
        console.warn('trackFlowComplete failed', { error: e })
      }

      setRegisteredEmail(data.email)
    } catch (error) {
      console.error('Webinar registration failed', { error })
      setError('root', { message: 'Something went wrong. Please try again.' })
    }
  }

  if (registeredEmail) {
    return (
      <div className="flex flex-col gap-4">
        <h3 className="text-body-bold-lg text-foreground">You&rsquo;re on the list.</h3>
        {/*
          The joining link is delivered by Customer.io, keyed off the
          `lead_magnet_slug` trait on the Lead this form creates (organic vs paid).
          Deliberately no join button here — the email is the only entry point, so
          the copy below is the whole promise.
        */}
        <p className="text-body-sm text-foreground">
          We&rsquo;ll send the joining link and a calendar invite to <>{registeredEmail} before the webinar.</>
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label htmlFor="webinar-name" className="text-body-sm-bold text-foreground">
          Name
        </label>
        <Input id="webinar-name" {...register('name')} error={errors.name?.message ?? null} />
      </div>

      <Controller
        name="household_income_range"
        control={control}
        render={({ field }) => (
          <RangeSelectInput
            id="webinar-household-income"
            label="Household income"
            name={field.name}
            ref={field.ref}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            options={INCOME_RANGE_OPTIONS}
            placeholder="Select a range"
            labelClassName={LABEL_CLASS}
            className={SELECT_CLASS}
            error={errors.household_income_range?.message}
          />
        )}
      />

      <Controller
        name="investable_assets_range"
        control={control}
        render={({ field }) => (
          <RangeSelectInput
            id="webinar-investable-assets"
            label="Investable assets outside retirement accounts"
            name={field.name}
            ref={field.ref}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            options={ASSET_RANGE_OPTIONS}
            placeholder="Select a range"
            labelClassName={LABEL_CLASS}
            className={SELECT_CLASS}
            error={errors.investable_assets_range?.message}
          />
        )}
      />

      <div className="flex flex-col gap-2">
        <label htmlFor="webinar-email" className="text-body-sm-bold text-foreground">
          Email
        </label>
        <Input id="webinar-email" type="email" {...register('email')} error={errors.email?.message ?? null} />
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="webinar-phone" className="text-body-sm-bold text-foreground">
          Phone
        </label>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <NumberInput
              id="webinar-phone"
              mask="phone"
              name={field.name}
              ref={field.ref}
              value={field.value ?? ''}
              onBlur={field.onBlur}
              onChange={(e) => field.onChange(e.target.value)}
              error={errors.phone?.message ?? null}
            />
          )}
        />
      </div>

      {/*
        Rendered unconditionally, not gated on phone entry: the consent language has
        to be on screen while the visitor is deciding whether to hand over a number,
        not revealed after they have started typing one.
      */}
      <div className="text-neutral-darker">
        {/* text-body-xs (11px) steps the fine print down from the default text-body-sm. */}
        <PhoneDisclosure ctaText={CTA_TEXT} className="text-body-xs" />
      </div>

      {/*
        `cta-sm` ships text-body-xs (11px); text-body-sm steps the label up so the CTA
        outweighs the fine print above it. cn() in Button resolves the font-size conflict.
      */}
      <Button type="submit" variant="funnel" size="cta-sm" disabled={isSubmitting} className="w-full text-body-sm">
        {isSubmitting ? 'Saving...' : CTA_TEXT}
      </Button>

      {errors.root && (
        <p className="text-body-sm text-red" role="alert">
          {errors.root.message}
        </p>
      )}
    </form>
  )
}
